import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Popup } from '../../../core/shared/components/popup/popup';
import { ToastService } from '../../../core/shared/services/toast.service';
import { CraditService } from '../shared/services/cradit.service';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { RichTextEditor } from '../shared/components/rich-text-editor/rich-text-editor';
import { SectionDragHandle } from '../shared/components/section-drag-handle/section-drag-handle';
import { RichTextPipe } from '../shared/utils/rich-text.pipe';
import {
  hasRichTextContent,
  isRichTextHtml,
  richTextToPlainText,
  sanitizeRichText,
} from '../shared/utils/rich-text.util';


interface RecommendationSectionData {
  title: string;
  paragraphs: string[];
}

@Component({
  selector: 'app-recommendations-section',
  imports: [
    CommonModule,
    FormsModule,
    Popup,
    TranslatePipe,
    RichTextEditor,
    RichTextPipe,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    SectionDragHandle,
  ],
  templateUrl: './recommendations-section.html',
  styleUrl: './recommendations-section.css',
})
export class RecommendationsSection
  implements OnInit, OnChanges, OnDestroy
{
  @Input() sectionId = 'recommendations';
  @Input() editable = false;
  @Input() refGrp = 28;
  @Input() refValSeq = 2532;

  onboardingId!: number;

  crdtPrpslSeq: number | null = null;
  crdtPrpslBlkSeq: number | null = null;

  sectionTitle = 'Recommendations';
  paragraphs: string[] = [''];

  saving = false;
  errorMessage = '';
  successMessage = '';

  showDeletePopup = false;
  deleteMessage = '';
  private pendingDeleteIndex: number | null = null;

  private saveSub?: Subscription;
  private seqSub?: Subscription;
  private initialized = false;

  constructor(
    private creditService: CraditService,
    private route: ActivatedRoute,
    private toast: ToastService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.onboardingId = Number(
      this.route.snapshot.paramMap.get('id') ||
        this.route.parent?.snapshot.paramMap.get('oid'),
    );

    this.initialized = true;

    this.seqSub = this.creditService.crdtPrpslSeq$.subscribe((seq) => {
      this.crdtPrpslSeq = seq;
      this.loadSection();
    });

    this.saveSub = this.creditService.saveSectionTrigger$.subscribe(
      (sectionId) => {
        if (sectionId === this.sectionId) {
          this.saveSection();
        }
      },
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const sectionChanged =
      changes['sectionId'] ||
      changes['refValSeq'] ||
      changes['refGrp'];

    if (!this.initialized || !sectionChanged) {
      return;
    }

    this.crdtPrpslBlkSeq = null;
    this.sectionTitle = 'Recommendations';
    this.paragraphs = [''];

    this.loadSection();
  }

  ngOnDestroy(): void {
    this.saveSub?.unsubscribe();
    this.seqSub?.unsubscribe();
  }

  loadSection(): void {
    if (!this.crdtPrpslSeq || !this.refValSeq) {
      this.sectionTitle = 'Recommendations';
      this.paragraphs = [''];
      return;
    }

    const requestedSectionId = this.sectionId;
    const requestedRefValSeq = this.refValSeq;

    this.creditService
      .getCrdtBlck(this.crdtPrpslSeq, [requestedRefValSeq])
      .subscribe({
        next: (res: any[]) => {
          if (
            requestedSectionId !== this.sectionId ||
            requestedRefValSeq !== this.refValSeq
          ) {
            return;
          }

          if (!res?.length) {
            this.crdtPrpslBlkSeq = null;
            this.sectionTitle = 'Recommendations';
            this.paragraphs = [''];
            return;
          }

          const row = res[0];

          this.crdtPrpslBlkSeq = row?.crdtPrpslBlkSeq ?? null;

          const data = Array.isArray(row?.jsonData) ? row.jsonData[0] : null;

          this.sectionTitle =
            String(data?.title ?? 'Recommendations').trim() || 'Recommendations';

          const savedParagraphs = Array.isArray(data?.paragraphs)
            ? data.paragraphs
                .map((item: any) => String(item ?? '').trim())
                .filter(Boolean)
            : [];

          if (savedParagraphs.length > 0) {
            this.paragraphs = savedParagraphs;
            return;
          }

          const fallbackText = String(row?.txtData ?? '').trim();

          if (fallbackText) {
            this.paragraphs = fallbackText
              .split(/\n\s*\n/)
              .map((item) => item.trim())
              .filter(Boolean);

            if (this.paragraphs.length === 0) {
              this.paragraphs = [''];
            }

            return;
          }

          this.paragraphs = [''];
        },

        error: (error) => {
          console.error('Load recommendations section error:', error);
          this.sectionTitle = 'Recommendations';
          this.paragraphs = [''];
        },
      });
  }

  saveSection(): void {
    this.errorMessage = '';
    this.successMessage = '';
    this.saving = true;

    const cleanedParagraphs = this.paragraphs
      .filter((item) => hasRichTextContent(item))
      .map((item) => (isRichTextHtml(item) ? sanitizeRichText(item) : String(item).trim()));

    const payload = {
      crprtOnbrdngSeq: this.onboardingId,
      crdtPrpslSeq: this.crdtPrpslSeq,

      blockList: [
        {
          crdtPrpslBlkSeq: this.crdtPrpslBlkSeq ?? null,
          refGrp: this.refGrp,
          refValSeq: this.refValSeq,
          blkTyp: 3,
          delFlg: 0,
          sortOrdr: 1,
          txtData: cleanedParagraphs.map((item) => richTextToPlainText(item)).join('\n\n'),
          jsonData: [
            {
              title: this.sectionTitle?.trim() || 'Recommendations',
              paragraphs: cleanedParagraphs,
            },
          ],
        },
      ],
    };

    this.creditService.saveSection(payload).subscribe({
      next: (res: any) => {
        this.saving = false;
        this.successMessage = this.translate.instant('CREDIT_RECOMMENDATIONS.SAVED_SUCCESS');

        this.toast.success(this.successMessage);

        this.creditService.setCrdtPrpslSeq(res?.crdtPrpslSeq);

        this.crdtPrpslBlkSeq =
          res?.crdtPrpslBlkSeq ?? this.crdtPrpslBlkSeq;

        this.loadSection();

        this.creditService.reportSectionSaveResult({
          sectionId: this.sectionId,
          status: 'saved',
        });
      },

      error: (error) => {
        this.saving = false;

        console.error('Save recommendations section error:', error);

        this.errorMessage =
          error?.error?.message ?? this.translate.instant('CREDIT_RECOMMENDATIONS.SAVE_FAILED');

        this.creditService.reportSectionSaveResult({
          sectionId: this.sectionId,
          status: 'api-error',
          message: this.errorMessage,
        });
      },
    });
  }

  addParagraph(): void {
    this.paragraphs = [...this.paragraphs, ''];
  }

  requestRemoveParagraph(index: number): void {
    this.pendingDeleteIndex = index;
    this.deleteMessage = 'CREDIT_RECOMMENDATIONS.DELETE_CONFIRM';
    this.showDeletePopup = true;
  }

  confirmRemoveParagraph(): void {
    if (this.pendingDeleteIndex === null) return;

    this.removeParagraph(this.pendingDeleteIndex);
    this.pendingDeleteIndex = null;
    this.toast.success('CREDIT_RECOMMENDATIONS.PARAGRAPH_DELETED');
  }

  private removeParagraph(index: number): void {
    this.paragraphs.splice(index, 1);

    if (this.paragraphs.length === 0) {
      this.paragraphs = [''];
    }

    this.paragraphs = [...this.paragraphs];
  }

  /** Drag-and-drop reorder; the saved paragraphs array keeps this order. */
  onParagraphDrop(event: CdkDragDrop<unknown>): void {
    const reordered = [...this.paragraphs];
    moveItemInArray(reordered, event.previousIndex, event.currentIndex);
    this.paragraphs = reordered;
  }

  trackByIndex(index: number): number {
    return index;
  }

  get activeParagraphs(): string[] {
    return this.paragraphs.filter((item) => hasRichTextContent(item));
  }

  hasContent(value: string | null | undefined): boolean {
    return hasRichTextContent(value);
  }
}